import { useApi } from '~/composables/useApi';
import type { LocationQueryRaw } from 'vue-router';

type AppI18n = {
  setLocale: (locale: string) => Promise<void> | void;
  locale?: { value?: string } | string;
};

export const APP_LOCALES = ['ko', 'en'] as const;
export type AppLocale = (typeof APP_LOCALES)[number];

export const normalizeAppLocale = (value: unknown, fallback: AppLocale = 'ko'): AppLocale => {
  const lang = String(value ?? '').trim();
  return APP_LOCALES.includes(lang as AppLocale) ? (lang as AppLocale) : fallback;
};

/** route.query.lang 파싱 */
export const parseRouteLangQuery = (value: unknown): AppLocale | null => {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw === 'ko' || raw === 'en' ? raw : null;
};

/** query 객체에 lang 병합 (기존 lang 유지 또는 fallback 주입) */
export const withAppLocaleQuery = (
  query: LocationQueryRaw = {},
  lang?: AppLocale,
): LocationQueryRaw => {
  const existing = parseRouteLangQuery(query.lang);
  const resolved = lang ?? existing ?? getCurrentAppLocale();
  return { ...query, lang: resolved };
};

/** 현재 라우트 URL에 lang 쿼리 반영 (페이지 이동·초기 로드 후 세션/레거시 동기화용) */
export const syncAppLocaleRoute = async (lang?: AppLocale) => {
  if (!import.meta.client) return;

  const router = useRouter();
  const route = router.currentRoute.value;
  const resolved = lang ?? parseRouteLangQuery(route.query.lang) ?? getCurrentAppLocale();

  if (parseRouteLangQuery(route.query.lang) === resolved) {
    return;
  }

  await router.replace({
    path: route.path,
    query: withAppLocaleQuery({ ...route.query }, resolved),
    hash: route.hash || undefined,
  });
};

/** URL ?lang= (레거시 JSP changeLang과 동일 — 사용자 선택 최우선) */
export const getQueryAppLocale = (): AppLocale | null => {
  if (!import.meta.client) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang');
  return lang === 'ko' || lang === 'en' ? lang : null;
};

export const getCurrentAppLocale = (): AppLocale => {
  if (!import.meta.client) {
    return 'ko';
  }

  const nuxtApp = useNuxtApp();
  const i18n = nuxtApp.$i18n as AppI18n;
  const current = typeof i18n.locale === 'object' ? i18n.locale?.value : i18n.locale;
  return normalizeAppLocale(current);
};

/** URL 쿼리 > 서버 userInfo.lang (브라우저/i18n 쿠키보다 세션 언어 우선) */
export const resolveAppLocale = (serverLang?: unknown): AppLocale => {
  const fromQuery = getQueryAppLocale();
  if (fromQuery) {
    return fromQuery;
  }

  return normalizeAppLocale(serverLang);
};

/** API 요청 query용 lang (URL ?lang= > i18n locale) */
export const getAppLocaleQuery = (): { lang: AppLocale } => ({
  lang: getQueryAppLocale() ?? getCurrentAppLocale(),
});

/**
 * 화면 이동 시 lang을 함께 넘긴다.
 * 쿼리 없는 navigateTo는 locale 미들웨어가 같은 경로로 한 번 더 보내고,
 * 그 재이동이 진행 중인 화면 마운트를 취소해 본문이 빈 채로 남는다.
 */
export const navigateWithAppLocale = (
  to: string | { path: string; query?: LocationQueryRaw; hash?: string },
) => {
  const lang = getQueryAppLocale() ?? getCurrentAppLocale();

  if (typeof to === 'string') {
    return navigateTo({
      path: to,
      query: { lang },
    });
  }

  return navigateTo({
    path: to.path,
    query: withAppLocaleQuery(to.query ?? {}, lang),
    hash: to.hash,
  });
};

/** API 요청 시 세션/URL 기준 lang 파라미터 부여 (POST body) */
export const appendAppLocaleToParams = (params: URLSearchParams) => {
  if (params.has('lang')) return params;
  params.append('lang', getAppLocaleQuery().lang);
  return params;
};

let localeSessionSyncPromise: Promise<void> | null = null;

/** i18n/URL lang 기준으로 selectUserInfo.do 세션 동기화 (최초 1회) */
export const ensureAppLocaleSessionSync = async () => {
  if (!import.meta.client) return;

  const lang = getAppLocaleQuery().lang;
  if (!localeSessionSyncPromise) {
    localeSessionSyncPromise = useApi('/api/common/selectUserInfo.do', {
      method: 'POST',
      query: { lang },
      skipLoading: true,
    })
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        localeSessionSyncPromise = null;
      });
  }
  await localeSessionSyncPromise;
};

/** 레거시 iframe → Nuxt SPA locale 동기화 (iframe 자체 reload는 하지 않음) */
export const syncAppLocaleFromLegacy = async (lang: string) => {
  if (!import.meta.client) return;

  const normalized = normalizeAppLocale(lang);
  await setAppLocale(normalized);
  await syncAppLocaleRoute(normalized);
  await ensureAppLocaleSessionSync();

  try {
    const { useUserStore } = await import('~/composables/user/useUserStore');
    const { selectedLang } = useUserStore();
    selectedLang.value = normalized;

    const { useAuthStore } = await import('~/stores/useAuthStore');
    const authStore = useAuthStore();
    if (authStore.user) {
      (authStore.user as Record<string, unknown>).lang = normalized;
    }

    const { useMenuStore } = await import('~/stores/useMenuStore');
    const menuStore = useMenuStore();
    await menuStore.refreshMenuList({ userAuthorities: authStore.authorities });

    const { notifyHrEvalCalculationLocaleChanged } = await import(
      '~/composables/hr-evaluation/calculation/useHrEvalCalculationLocaleRefresh'
    );
    notifyHrEvalCalculationLocaleChanged();
  } catch (e) {
    console.error('레거시 locale 동기화 후처리 실패:', e);
  }
};

/** setup 컨텍스트 밖(store·비동기 handler)에서 locale 변경 */
export const setAppLocale = async (locale: string) => {
  if (!import.meta.client) {
    return;
  }

  const normalized = normalizeAppLocale(locale);
  if (getCurrentAppLocale() === normalized) {
    return;
  }

  const nuxtApp = useNuxtApp();
  const i18n = nuxtApp.$i18n as AppI18n;
  await i18n.setLocale(normalized);

  if (import.meta.client) {
    window.dispatchEvent(new CustomEvent('appLocaleChanged', { detail: { lang: normalized } }));
  }
};
